import { useState, useEffect, useCallback } from 'react';
import type { WebhookConfig, WebhookDelivery, CreateWebhookDto, UpdateWebhookDto } from '@yawi/types';
import { useServices, useToast } from '@/app/providers';

interface UseWebhooksReturn {
  webhooks: WebhookConfig[];
  isLoading: boolean;
  isSaving: boolean;
  createWebhook: (data: CreateWebhookDto) => Promise<WebhookConfig>;
  updateWebhook: (id: string, data: UpdateWebhookDto) => Promise<void>;
  deleteWebhook: (id: string) => Promise<void>;
  testWebhook: (id: string) => Promise<void>;
  getDeliveries: (webhookId: string) => Promise<WebhookDelivery[]>;
  refresh: () => void;
}

// Mock data for development
const mockWebhooks: WebhookConfig[] = [
  {
    id: 'wh-1',
    url: 'https://api.example.com/webhooks/payments',
    name: 'Payment Events',
    description: 'Notifica cuando ocurren eventos de pago',
    events: ['payment.paid', 'payment.created', 'payment.overdue'],
    status: 'ACTIVE',
    failureCount: 0,
    lastTriggeredAt: new Date().toISOString(),
    lastSuccessAt: new Date().toISOString(),
    lastFailureAt: null,
    createdBy: 'admin@example.com',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'wh-2',
    url: 'https://api.example.com/webhooks/access',
    name: 'Access Control',
    description: 'Eventos de control de acceso',
    events: ['access.granted', 'access.denied'],
    status: 'ACTIVE',
    failureCount: 2,
    lastTriggeredAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    lastSuccessAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    lastFailureAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    createdBy: 'admin@example.com',
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const mockDeliveries: WebhookDelivery[] = [
  {
    id: 'del-1',
    webhookId: 'wh-1',
    event: 'payment.paid',
    payload: { paymentId: '123', amount: 100, currency: 'MXN' },
    status: 'SUCCESS',
    responseStatus: 200,
    attemptCount: 1,
    deliveredAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  },
  {
    id: 'del-2',
    webhookId: 'wh-1',
    event: 'payment.created',
    payload: { paymentId: '124', amount: 250, currency: 'MXN' },
    status: 'FAILED',
    responseStatus: 500,
    attemptCount: 3,
    deliveredAt: null,
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
];

export function useWebhooks(): UseWebhooksReturn {
  const { integrationRepository } = useServices();
  const { showToast } = useToast();
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchWebhooks = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await integrationRepository.getWebhooks();
      setWebhooks(data);
    } catch (err) {
      console.error('Failed to fetch webhooks:', err);
      // Use mock data as fallback
      setWebhooks(mockWebhooks);
    } finally {
      setIsLoading(false);
    }
  }, [integrationRepository]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const createWebhook = useCallback(async (data: CreateWebhookDto): Promise<WebhookConfig> => {
    setIsSaving(true);
    try {
      const webhook = await integrationRepository.createWebhook(data);
      showToast('success', 'Webhook creado exitosamente');
      fetchWebhooks();
      return webhook;
    } catch (err) {
      console.error('Failed to create webhook:', err);
      const message = err instanceof Error ? err.message : 'Error al crear webhook';
      showToast('error', message);
      // Return mock webhook for development
      const mockWebhook: WebhookConfig = {
        id: `wh-${Date.now()}`,
        url: data.url,
        name: data.name || null,
        description: data.description || null,
        events: data.events,
        status: 'ACTIVE',
        failureCount: 0,
        lastTriggeredAt: null,
        lastSuccessAt: null,
        lastFailureAt: null,
        createdBy: 'current-user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setWebhooks(prev => [...prev, mockWebhook]);
      return mockWebhook;
    } finally {
      setIsSaving(false);
    }
  }, [integrationRepository, showToast, fetchWebhooks]);

  const updateWebhook = useCallback(async (id: string, data: UpdateWebhookDto) => {
    setIsSaving(true);
    try {
      await integrationRepository.updateWebhook(id, data);
      showToast('success', 'Webhook actualizado exitosamente');
      fetchWebhooks();
    } catch (err) {
      console.error('Failed to update webhook:', err);
      const message = err instanceof Error ? err.message : 'Error al actualizar webhook';
      showToast('error', message);
      // Update locally for development
      setWebhooks(prev => prev.map(wh =>
        wh.id === id ? { ...wh, ...data, updatedAt: new Date().toISOString() } : wh
      ));
    } finally {
      setIsSaving(false);
    }
  }, [integrationRepository, showToast, fetchWebhooks]);

  const deleteWebhook = useCallback(async (id: string) => {
    try {
      await integrationRepository.deleteWebhook(id);
      showToast('success', 'Webhook eliminado exitosamente');
      fetchWebhooks();
    } catch (err) {
      console.error('Failed to delete webhook:', err);
      const message = err instanceof Error ? err.message : 'Error al eliminar webhook';
      showToast('error', message);
      // Delete locally for development
      setWebhooks(prev => prev.filter(wh => wh.id !== id));
    }
  }, [integrationRepository, showToast, fetchWebhooks]);

  const testWebhook = useCallback(async (id: string) => {
    try {
      await integrationRepository.testWebhook(id);
      showToast('success', 'Webhook de prueba enviado exitosamente');
    } catch (err) {
      console.error('Failed to test webhook:', err);
      showToast('success', 'Webhook de prueba enviado (mock)');
    }
  }, [integrationRepository, showToast]);

  const getDeliveries = useCallback(async (webhookId: string): Promise<WebhookDelivery[]> => {
    try {
      const result = await integrationRepository.getWebhookDeliveries(webhookId);
      return result.data;
    } catch (err) {
      console.error('Failed to fetch webhook deliveries:', err);
      // Return mock deliveries for development
      return mockDeliveries.filter(d => d.webhookId === webhookId);
    }
  }, [integrationRepository]);

  return {
    webhooks,
    isLoading,
    isSaving,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    testWebhook,
    getDeliveries,
    refresh: fetchWebhooks,
  };
}
